import React, { useEffect, useState } from 'react';
import { OVERLAY_PANEL_GAP } from '../../pages/Shoreline/theme';
import { useDispatch, useSelector } from 'react-redux';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import { Actions as Map } from '../../../store/ducks/map';
import { Waves, GestureOutlined, LinearScale } from '@material-ui/icons';
import ReactGA from 'react-ga';
import { Actions as Imagery } from '../../../store/ducks/imagery';
import {
  Box,
  Grow,
  Card,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import * as turf from '@turf/turf';
import { TerraDrawManager } from '../../../common/TerraDrawManager';
import ShapeEditControl from '../map/ShapeEditControl';
import { BASELINE_VERTEX_EDITING_ENABLED, useShapeVertexEditing } from '../../../hooks/useShapeVertexEditing';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import SimpleInfoDialog from '../dialog/SimpleInfoDialog';
import { generateOrthogonalTransects } from '../../../algorithms/shoreline';
import ee from '../../../services/earth-engine';

const MIN_TRANSECT_STEP = 10;
const MAX_TRANSECT_STEP = 1000;
const MIN_TRANSECT_EXTENT = 50;
const MAX_TRANSECT_EXTENT = 5000;
const TRANSECT_CONFIG = {
  CIRCLE_RADIUS: 1,
  POLYLINE_STYLE: {
    strokeColor: 'purple',
    strokeOpacity: 1.0,
    strokeWeight: 3,
    geodesic: true,
    clickable: true,
    zIndex: 90,
  },
};
/**
 *
 * @function CoastlineAnalysisSteps
 * @description
 * Component that manages the steps for drawing transects on a map and performing coastline analysis.
 * @param {Object} props
 * @param {Function} props.t - Translation function for internationalization.
 * @param {Object} props.mapRef - Reference to the Google Maps instance.
 * @param {Object} props.roiCoords - Coordinates of the region of interest polygon.
 * @param {Object} props.styles - Styles for the component.
 * @param {Object} props.bounds - Bounds of the map view.
 * @param {Object} props.baseLine - Baseline object containing overlay and properties.
 * @param {Function} props.setBaseLine - Function to update the baseline state.
 * @param {Object} props.drawingManager - Reference to the Google Maps Drawing Manager instance.
 * **Called by:
 * @see {ProcessingPage} - Shoreline's result page (Shoreline/result.jsx).
 * @memberof module:Shoreline
 * @returns {JSX.Element} Coastline Analysis steps JSX element
 */
const CoastlineAnalysisSteps = ({ t, mapRef, roiCoords, styles, bounds, baseLine, setBaseLine, drawingManager }) => {
  const [isSettingsTransectOpen, setIsSettingsTransectOpen] = useState(false);
  const [step, setStep] = useState('SELECT_IMAGES'); // 'SELECT_IMAGES' | 'DRAW_BASELINE' | 'CONFIGURE_TRANSECTS' | 'ANALYSIS_DONE'
  const [confirmDrawing, setConfirmDrawing] = useState(false);
  const [transectsStep, setTransectsStep] = useState(400);
  const [transectsExtent, setTransectsExtent] = useState(1600);
  const [transectsObjects, setTransectsObjects] = useState([]);
  const [showImageChooserHelp, setShowImageChooserHelp] = React.useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [threshold, setThreshold] = useState(0.01);
  const [baseLineCoords, setBaseLineCoords] = useState([]);
  const [confirmTransectsConfigEnabled, setConfirmTransectsConfigEnabled] = useState(true);
  const hasShorelineData = useSelector((state) => Boolean(state.results.coastlineData));
  const selectedImages = useSelector((state) => state.results.chosenImages);
  const dispatch = useDispatch();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const errorAnalysis = useSelector((state) => state.imagery.error);

  /**
   * @function generateTransectsPreview
   * @description Generates and draws a live preview of the orthogonal transects on the map,
   * based on the current step/extent settings and the drawn baseline.
   * 1. Validates the step/extent values against their min/max bounds.
   * 2. Clears any previously drawn preview transects from the map.
   * 3. Generates the orthogonal transects for the current baseline and evaluates them.
   * 4. Draws each transect as a Google Maps Polyline and stores the references for later cleanup.
   * @see {@link generateOrthogonalTransects} - computes the transect geometries.
   * **Called by:
   * @see {CoastlineAnalysisSteps} - via the step/extent inputs' onChange/onBlur.
   * @memberof module:Shoreline
   * @returns {Function|void} A cleanup function that cancels a pending evaluate() callback, when one was scheduled.
   */
  const generateTransectsPreview = () => {
    if (!transectsExtent || !transectsStep) return;
    const isInvalid =
      transectsExtent < MIN_TRANSECT_EXTENT ||
      transectsExtent > MAX_TRANSECT_EXTENT ||
      transectsStep < MIN_TRANSECT_STEP ||
      transectsStep > MAX_TRANSECT_STEP;
    setConfirmTransectsConfigEnabled(!isInvalid);
    if (baseLineCoords.length < 2) return;
    transectsObjects.forEach((object) => object.setMap(null));
    setTransectsObjects([]);
    if (isInvalid) return;
    const eeTransects = generateOrthogonalTransects(baseLineCoords, transectsStep, transectsExtent);
    let cancelled = false;
    ee.List(eeTransects).evaluate((transectList, error) => {
      if (cancelled) return;
      if (error) return console.error('Error evaluating transects', error);
      let tempTransectsObjects = [];
      transectList.forEach((object) => {
        const coords = object.geometry.coordinates;
        const path = coords.map((arr) => ({
          lat: arr[1],
          lng: arr[0],
        }));
        const polyline = new window.google.maps.Polyline({
          path,
          transect: object,
          ...TRANSECT_CONFIG.POLYLINE_STYLE,
        });
        polyline.setMap(mapRef.current);
        tempTransectsObjects.push(polyline);
      });
      setTransectsObjects(tempTransectsObjects);
    });
    return () => {
      cancelled = true;
    };
  };

  useEffect(() => {
    if (!errorAnalysis) return;
    notifyError(t('forms.shorelineAnalysis.errors.invalidTransectError'));
    console.error('ERROR ANALYSIS ', errorAnalysis);
    restartAnalysis();
  }, [errorAnalysis]);

  useEffect(() => {
    if (selectedImages?.length > 0) setStep('DRAW_BASELINE');
  }, [selectedImages?.length]);

  useEffect(() => {
    if (hasShorelineData && selectedImages?.length > 0) setStep('ANALYSIS_DONE');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * @function handleRedo
   * @description
   * Clear current baseline to draw/create it again.
   * 1. Removes all event listeners from the current drawing manager
   * 2. Clears the drawing manager from the map
   * 3. Removes the existing baseline overlay from the map
   * 4. Resets state variables to allow new baseline creation
   * 5. Reinitializes the drawing manager so the user can draw the baseline again
   *
   * @see {@link handleDrawBaseline} - Called to reinitialize the drawing manager
   * **Called by:
   * @see {CoastlineAnalysisSteps} - via the "Redo" button's onClick.
   * @memberof module:Shoreline
   * @returns {void}
   */

  /**
   * @description Lets the user drag the drawn baseline's existing points, re-checking after each
   * adjustment that it still lies inside the AOI — the same rule `handleDrawing` enforces when the
   * baseline is first drawn.
   * @see {@link useShapeVertexEditing}
   * @see {@link areBaselineCoordinatesInsideAOI}
   * @memberof module:Shoreline
   */
  const baselineEditing = useShapeVertexEditing({
    onChange: setBaseLineCoords,
    validate: (coords) => (areBaselineCoordinatesInsideAOI(coords) ? '' : t('forms.map.editOutsideAOI')),
    enabled: BASELINE_VERTEX_EDITING_ENABLED,
  });

  /**
   * @function areBaselineCoordinatesInsideAOI
   * @description Checks whether every `[lng, lat]` pair falls inside the region of interest. The
   * coordinate-based counterpart of `isPolygonInsideAOI`, used while editing, when the new vertices
   * arrive as plain coordinates rather than as an overlay.
   * @param {Array<Array<Number>>} coords - Baseline coordinates as [lng, lat] pairs.
   * @see {@link isPolygonInsideAOI}
   * @memberof module:Shoreline
   * @returns {Boolean} True when the whole baseline is inside the AOI.
   */
  function areBaselineCoordinatesInsideAOI(coords) {
    if (!roiCoords.current) return true;

    return coords.every(([lng, lat]) =>
      window.google.maps.geometry?.poly.containsLocation(new window.google.maps.LatLng(lat, lng), roiCoords.current),
    );
  }
  const handleRedo = () => {
    if (drawingManager.current) {
      drawingManager.current.cleanup();
      drawingManager.current.setMap(null);
      drawingManager.current = null;
    }
    baseLine && baseLine.overlay.setMap(null);
    baselineEditing.setShape(null);
    setBaseLine(null);
    setStep('DRAW_BASELINE');
    setConfirmDrawing(false);
    handleDrawBaseline();
  };

  /**
   * @function handleDrawing
   * @description - After drawing the baseline, this function receives the polyline, validates and update states
   * 1.Checks if the polyline is inside the Area of Interest (AOI)
   * 2. If valid: saves the baseline, disables drawing mode, and updates application state
   * 3. If invalid: removes the polyline from the map (user must redraw)
   * @param {Object} polyg - Drawing event object from Google Maps Drawing Manager. It contains the overlay (polyg.overlay) which is the Google Maps polyline
   * @see {@link isPolygonInsideAOI} - Called to check if polyline geometry is inside the Area of Interest
   * **Called by:
   * @see {handleDrawBaseline} - registered as the drawing manager's 'overlaycomplete' event listener.
   * @memberof module:Shoreline
   * @returns {void}
   */
  function handleDrawing(polyg) {
    if (isPolygonInsideAOI(polyg.overlay, roiCoords.current)) {
      setConfirmDrawing(true);
      setBaseLine(polyg);
      if (drawingManager.current) drawingManager.current.setDrawingMode(null);
      dispatch(Map.completeDrawing());
      var coords = polyg.overlay
        .getPath()
        .getArray()
        .map((coord) => [coord.lng(), coord.lat()]);
      setBaseLineCoords(coords);
      baselineEditing.setShape(polyg.overlay, 'polyline');
      return;
    }
    notifyError(t('forms.shorelineAnalysis.baselineAOIDrawAlert'));
    polyg.overlay.setMap(null);
  }

  /**
   * @function isPolygonInsideAOI
   * @description
   * Validates if one polygon is fully contained within another polygon.
   * 1. Iterates over all vertices of the inner polygon
   * 2. Checks if each vertex is contained within the outer polygon
   * 3. Returns `true` if fully contained, otherwise `false`
   *
   * @param {Object} polygonInner = Polygon that must be inside, ex : the baseline.
   * @param {Object} polygonOuter = Polygon that must contain the other one, ex: the area of interest (AOI)
   * **Called by:
   * @see {handleDrawing} - This function calls `isPolygonInsideAOI` to validate the baseline geometry.
   * @memberof module:Shoreline
   * @returns {Boolean} True if the inner polygon is fully inside the outer one, else False
   *
   * @example validate baseline polygon inside AOI
   * const isValid = isPolygonInsideAOI(baselinePolygon, aoiPolygon);
   * if (isValid) {saveBaseline(baselinePolygon);}
   * else  {baselinePolygon.setMap(null); } // force user to redraw
   */
  function isPolygonInsideAOI(polygonInner, polygonOuter) {
    for (let i = 0; i < polygonInner.getPath().getLength(); i++) {
      if (!window.google.maps.geometry?.poly.containsLocation(polygonInner.getPath().getAt(i), polygonOuter)) {
        return false;
      }
    }
    return true;
  }

  /**
   * @function handleDrawBaseline
   * @description
   * Prepares and enables baseline drawing on the map using the Google Maps DrawingManager.
   * 1. Creates a DrawingManager instance if it does not already exist, configured for polyline drawing
   * 2. Attaches the DrawingManager to the current map reference (`mapRef.current`)
   * 3. Registers an "overlaycomplete" listener that triggers `handleDrawing` when the user finishes drawing.
   * 4. Clears any previously drawn baseline from the map and resets its state.
   *
   * @see {@link handleDrawing} Called when "overlaycomplete" to validate and process the drawn baseline.
   * @requires GoogleMaps JavaScript API with the Drawing library loaded.
   * **Called by:
   * @see {handleRedo}
   * @see {CoastlineAnalysisSteps} - via the "draw baseline" button's onClick.
   * @memberof module:Shoreline
   * @returns {void}
   */
  const handleDrawBaseline = () => {
    if (!drawingManager.current) {
      drawingManager.current = new TerraDrawManager({
        drawingMode: 'polyline',
        drawingControl: false,
        polylineOptions: {
          strokeColor: '#03d4f0ff',
          strokeOpacity: 1,
          strokeWeight: 3,
        },
      });
      drawingManager.current.setMap(mapRef.current);
      drawingManager.current.addListener('overlaycomplete', handleDrawing);
    } else {
      drawingManager.current.setDrawingMode('polyline');
    }
    if (baseLine) baseLine.overlay.setMap(null);
    setBaseLine(null);
  };

  /**
   * @function importStepExtentTransects
   * @description
   * Imports transect coordinates from a KML file and calculates the extension and step distances.
   * 1. Get distance of each transect to determine the most common extension length
   * 2. Calculate midpoints of each transect to determine spacing (step) between them
   * 3. Sets the calculated extension and step values in state
   * @param {Array} allTransects - Array of transect coordinates imported from a KML file.
   * **Called by:
   * @see {readTransectsFileData} - This function calls `importStepExtentTransects` after reading transect data from a file.
   * @memberof module:Shoreline
   * @returns {void}
   *   */
  const importStepExtentTransects = (allTransects) => {
    if (!allTransects || allTransects.length === 0) {
      notifyError(t('forms.csqueeze.2.errorMessageNoValidCoord'));
      return;
    }
    const getMajority = (array) => {
      const count = array.reduce((acc, val) => {
        acc[val] = (acc[val] || 0) + 1;
        return acc;
      }, {});
      return Number(Object.keys(count).reduce((a, b) => (count[a] > count[b] ? a : b)));
    };
    let extensions = [];
    allTransects?.forEach((transect) => {
      extensions.push(
        turf.distance(turf.point(transect[0]), turf.point(transect[1]), {
          units: 'meters',
        }),
      );
    });
    const extension = Math.round(getMajority(extensions));
    const steps = [];
    const center_transects = allTransects?.map((transect) => {
      const coords_lng = (transect[0][0] + transect[1][0]) / 2;
      const coords_lat = (transect[0][1] + transect[1][1]) / 2;
      return [coords_lng, coords_lat];
    });
    if (center_transects.length < 2) {
      notifyError(t('forms.shorelineAnalysis.transectsSettings.alert.importTransectQuantity'));
      return;
    }
    for (let i = 0; i < center_transects.length - 1; i++) {
      const dist = turf.distance(center_transects[i], center_transects[i + 1], {
        units: 'meters',
      });
      steps.push(dist);
    }
    const step = Math.round(getMajority(steps));
    setTransectsExtent(extension);
    setTransectsStep(step);
  };

  /**
   * @function readFileAndGetCoordinates
   * @description
   * Parses a KML file content string to extract coordinate data.
   * 1. Splits the file content by "<coordinates>" tags to isolate coordinate blocks
   * 2. Extracts and cleans coordinate strings, converting them to arrays of [lng, lat] pairs
   * 3. Returns an array of coordinate arrays, each representing a set of coordinates from the KML file
   * @param {string} fileContent - The raw text content of a KML file.
   * **Called by:
   * @see {readBaselineFileData} - This function calls `readFileAndGetCoordinates` to parse baseline coordinates from a KML file.
   * @see {readTransectsFileData} - This function calls `readFileAndGetCoordinates` to parse transect coordinates from a KML file.
   * @memberof module:Shoreline
   * @returns {Array<Array>} Array of coordinate arrays extracted from the KML file.
   * @example
   * const kmlContent = '<kml>...<coordinates>-48.5266,-27.4457,0 -48.5262,-27.4479,0</coordinates>...</kml>';
   * const coordinates = readFileAndGetCoordinates(kmlContent);
   * // coordinates = [[[-48.5266, -27.4457], [-48.5262, -27.4479]], ...]
   */

  const readFileAndGetCoordinates = (fileContent) => {
    if (fileContent.indexOf('<coordinates>') === -1) {
      notifyError(t('forms.csqueeze.2.errorMessageUnableInterpretKml'));
      return;
    }
    const coordinatesBlocks = fileContent.split('<coordinates>');
    coordinatesBlocks.shift();
    const allObj = [];
    coordinatesBlocks.forEach((block) => {
      let coordinatesString = block.split('</coordinates>')[0];
      let coordinatesArrayStrings = coordinatesString.split(/\s+/).map((el) => el.trim());
      let coordinatesArray = [];
      coordinatesArrayStrings.forEach((el) => {
        if (el.trim() !== '') {
          let coordinatesValues = el.split(',');
          coordinatesValues = coordinatesValues.map((el) => parseFloat(el));
          if (!isNaN(coordinatesValues[0]) && !isNaN(coordinatesValues[1])) {
            coordinatesArray.push([coordinatesValues[0], coordinatesValues[1]]);
          }
        }
      });
      if (coordinatesArray.length >= 2) allObj.push(coordinatesArray);
    });

    return allObj;
  };

  /**
   * @function readBaselineFileData
   * @description
   * Reads a KML file containing baseline coordinates, parses the coordinates,
   * and draws the baseline on the map.
   * @param {Object} e - The file input change event containing the selected file.
   * @see {@link readFileAndGetCoordinates} - Called to parse KML file content and extract coordinates.
   * @see {@link handleDrawing} - Called to process and display the drawn baseline on the map.
   * **Called by:
   * @see {CoastlineAnalysisSteps} - via the baseline KML file input's onChange.
   * @memberof module:Shoreline
   * @returns {void}
   */
  const readBaselineFileData = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    try {
      reader.onload = (event) => {
        const baselineFile = readFileAndGetCoordinates(reader.result, t, notifyError);
        if (baselineFile.length === 0) {
          notifyError(t('forms.csqueeze.2.errorMessageNoValidCoord'));
          return;
        }
        const formattedCoordinates = baselineFile[0].map((coord) => ({
          lat: coord[1],
          lng: coord[0],
        }));
        const polyline = new window.google.maps.Polyline({
          path: formattedCoordinates,
          strokeColor: '#03d4f0ff',
          strokeOpacity: 1,
          strokeWeight: 3,
          map: mapRef.current,
        });

        handleDrawing({ overlay: polyline });
      };
      if (file) reader.readAsText(file);
    } catch (error) {
      console.error('Error reading baseline file:', error);
      notifyError(t('forms.csqueeze.2.errorMessageUnableInterpretKml'));
    }
  };

  /**
   * @function readTransectsFileData
   * @description
   * Reads a KML file containing transect coordinates, parses the coordinates,
   * and imports them for analysis.
   * @param {Object} e - The file input change event containing the selected file.
   * @see {@link readFileAndGetCoordinates} - Called to parse KML file content and extract coordinates.
   * @see {@link importStepExtentTransects} - Called to process and import the transect coordinates.
   * **Called by:
   * @see {CoastlineAnalysisSteps} - via the transects KML file input's onChange.
   * @memberof module:Shoreline
   * @returns {void}
   */
  const readTransectsFileData = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    try {
      reader.onload = async (event) => {
        const allTransects = await readFileAndGetCoordinates(reader.result, t, notifyError);
        if (allTransects?.length === 0) {
          notifyError(t('forms.csqueeze.2.errorMessageNoValidCoord'));
          return;
        }
        importStepExtentTransects(allTransects);
      };
      if (file) reader.readAsText(file);
    } catch (error) {
      console.error('Error reading transects file:', error);
      notifyError(t('forms.csqueeze.2.errorMessageUnableInterpretKml'));
    }
  };

  /**
   * @function restartAnalysis
   * @description
   * Set the default value to all variables so the user can restaart the analysis. Removes drawn
   * overlays (baseline and transects) and clears active listeners from the DrawingManager.
   * **Called by:
   * @see {CoastlineAnalysisSteps} - via the "Restart analysis" button's onClick, and the errorAnalysis effect.
   * @memberof module:Shoreline
   * @returns {void}
   */
  const restartAnalysis = () => {
    setStep('SELECT_IMAGES');
    setTransectsExtent(0);
    setTransectsStep(0);
    setBaseLineCoords([]);
    setIsSettingsTransectOpen(false);
    baseLine && baseLine.overlay.setMap(null);
    setBaseLine(null);
    setConfirmDrawing(false);
    transectsObjects.forEach((object) => object.setMap(null));
    setTransectsObjects([]);
    dispatch(Imagery.restartAnalysis());
    if (drawingManager.current) {
      window.google.maps.event.clearInstanceListeners(drawingManager.current);
      drawingManager.current.setMap(null);
      drawingManager.current = null;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: OVERLAY_PANEL_GAP }}>
      <ShapeEditControl editing={baselineEditing} visible={confirmDrawing} />
      {step === 'ANALYSIS_DONE' && (
        <Card style={{ padding: 12 }} elevation={3} className={styles.card}>
          <Button
            color="secondary"
            variant="outlined"
            style={{
              width: '100%',
              height: 35,
              padding: 'inherit 20px',
            }}
            disabled={!hasShorelineData}
            onClick={restartAnalysis}>
            {t('forms.shorelineAnalysis.restartAnalysis').toUpperCase()}
          </Button>
        </Card>
      )}
      {step !== 'ANALYSIS_DONE' && !isSettingsTransectOpen && (
        <Card
          style={{
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
          elevation={3}>
          <div
            style={{
              fontWeight: 'bold',
              borderBottom: '1px solid gray',
              paddingBottom: 5,
              fontSize: 20,
              display: 'flex',
              alignItems: 'center',
            }}>
            {t('forms.imageChooser.actions.analyzeShoreline.title')}
            <IconButton
              onClick={() => {
                setShowHelpDialog(true);
              }}
              color="primary"
              size="small"
              style={{ marginLeft: 8 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </div>
          <SimpleInfoDialog
            open={showHelpDialog}
            close={() => {
              setShowHelpDialog(false);
            }}
            title={t('forms.transectEvolution.title')}
            text={t('forms.transectEvolution.TransectEvolutionInfoDialog')}
          />
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}>
            <Button
              style={{ justifyContent: 'left' }}
              disabled={step !== 'SELECT_IMAGES'}
              color="primary"
              startIcon={<Waves />}
              onClick={() => {
                ReactGA.event({
                  category: 'Actions',
                  action: 'analyzeCoastline',
                });
                dispatch(Imagery.imageSelection());
              }}>
              {t('forms.imageChooser.actions.analyzeShoreline.imageSelection.title')}
            </Button>
            <Button
              style={{ justifyContent: 'left' }}
              disabled={step !== 'DRAW_BASELINE'}
              color="primary"
              startIcon={<GestureOutlined />}
              onClick={handleDrawBaseline}>
              {t('forms.csqueeze.result.drawBaseline')}
            </Button>
            <Button
              style={{ justifyContent: 'left' }}
              disabled={step !== 'CONFIGURE_TRANSECTS'}
              color="primary"
              startIcon={<LinearScale />}
              onClick={() => {
                const center = bounds.current?.getCenter();
                mapRef.current.setCenter(center);
                setIsSettingsTransectOpen(true);
              }}>
              {t('forms.csqueeze.result.configTransects')}
            </Button>
          </Box>
        </Card>
      )}
      {!confirmDrawing && step === 'DRAW_BASELINE' && (
        <Card
          style={{
            width: '100%',
            padding: 12,
            textAlign: 'center',
            fontSize: 13,
          }}
          elevation={3}>
          {t('forms.csqueeze.result.tipBaseline')}
          {'\n'}
          <Button color="secondary" component="label" htmlFor="file_import_baseline_kml">
            {t('forms.shorelineAnalysis.transectsSettings.importBaseline')}
            <input
              style={{ display: 'none' }}
              id="file_import_baseline_kml"
              accept=".kml"
              type="file"
              onClick={(e) => (e.target.value = null)}
              onChange={readBaselineFileData}
            />
          </Button>
        </Card>
      )}
      <Grow in={step === 'DRAW_BASELINE'} unmountOnExit>
        <Box position="absolute" marginTop={6} paddingTop={2} justifyContent="center" width="100%" zIndex={99}>
          <Dialog
            open={confirmDrawing}
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
            PaperProps={{
              style: { padding: 20, borderRadius: 12, textAlign: 'center' },
            }}>
            <DialogTitle id="confirm-dialog-title" style={{ textAlign: 'center' }}>
              {t('forms.csqueeze.result.confirmBaseline')}
            </DialogTitle>
            <DialogContent style={{ fontSize: '16px' }}>
              <p>{t('forms.csqueeze.result.confirmBaseline')}</p>
            </DialogContent>
            <DialogActions style={{ justifyContent: 'center' }}>
              <Button onClick={handleRedo} color="primary">
                {t('forms.csqueeze.result.redo')}
              </Button>
              <Button
                onClick={() => {
                  setStep('CONFIGURE_TRANSECTS');
                  generateTransectsPreview();
                  setIsSettingsTransectOpen(true);
                  dispatch(Map.completeDrawing());
                }}
                color="primary"
                autoFocus>
                {t('forms.csqueeze.result.confirm')}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Grow>
      <Grow in={step === 'CONFIGURE_TRANSECTS' && isSettingsTransectOpen} unmountOnExit paddingTop={0}>
        <Box
          id="settingsTransects"
          display="contents"
          position="fixed !important"
          justifyContent="start"
          alignItems="center"
          width="100%"
          zIndex={99}
          style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
          <Card
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 5,
              padding: 12,
              textAlign: 'center',
            }}
            elevation={3}>
            <div
              style={{
                height: 20,
                display: 'flex',
                justifyContent: 'flex-end',
              }}>
              <Tooltip title={t('forms.imageChooser.helpTooltip')}>
                <IconButton onClick={() => setShowImageChooserHelp(true)} size="small" style={{ padding: 4 }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <SimpleInfoDialog
                open={showImageChooserHelp}
                close={() => {
                  setShowImageChooserHelp(false);
                }}
                title={t('forms.transectEvolution.title')}
                text={t('forms.transectEvolution.TransectEvolutionInfoDialog')}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 20,
              }}>
              <Typography id="discrete-slider" gutterBottom style={{ fontSize: 15 }}>
                {t('forms.shorelineAnalysis.transectsSettings.setStepLabel')}
              </Typography>
              <Input
                type="number"
                value={transectsStep === 0 ? '' : transectsStep}
                onChange={(e) => {
                  let value = Number(e.target.value);
                  setTransectsStep(isNaN(value) ? 0 : Number(value));
                }}
                onBlur={(e) => {
                  let value = Number(e.target.value);
                  generateTransectsPreview();
                }}
                placeholder="200"
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 20,
              }}>
              <Typography id="discrete-slider" gutterBottom style={{ fontSize: 15 }}>
                {t('forms.shorelineAnalysis.transectsSettings.setExtentLabel')}
              </Typography>
              <Input
                type="number"
                value={transectsExtent === 0 ? '' : transectsExtent}
                onChange={(e) => {
                  let value = Number(e.target.value);
                  setTransectsExtent(isNaN(value) ? 0 : value);
                }}
                onBlur={(e) => generateTransectsPreview()}
                placeholder="500"
              />
            </div>
            <p
              style={{
                padding: 0,
                margin: 0,
              }}>
              {t('forms.shorelineAnalysis.transectsSettings.or')}
            </p>
            <Button color="secondary" component="label" htmlFor="file_import_transects_kml">
              {t('forms.shorelineAnalysis.transectsSettings.importTransectsStepsExtent')}
              <input
                style={{ display: 'none' }}
                id="file_import_transects_kml"
                accept=".kml"
                type="file"
                onClick={(e) => (e.target.value = null)}
                onChange={readTransectsFileData}
              />
            </Button>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 20,
              }}>
              <Typography id="discrete-slider" gutterBottom style={{ fontSize: 15 }}>
                {t('forms.shorelineAnalysis.transectsSettings.setThresholdLabel')}
              </Typography>
              <Input
                type="number"
                value={threshold}
                onChange={(e) => {
                  let value = e.target.value;
                  if (value === '') return setThreshold('');
                  value = Number(value);
                  setThreshold(isNaN(value) ? 0 : value);
                }}
                placeholder="0.01"
              />
            </div>
            <Button
              disabled={!confirmTransectsConfigEnabled}
              color="primary"
              onClick={async () => {
                if (threshold === '') setThreshold(0.01);
                setIsSettingsTransectOpen(false);
                setStep('ANALYSIS_DONE');
                setConfirmDrawing(false);
                transectsObjects.forEach((object) => object.setMap(null));
                setTransectsObjects([]);
                await dispatch(
                  Imagery.performAnalysis(baseLineCoords, transectsStep, transectsExtent, selectedImages, threshold),
                );
              }}
              style={{
                paddingBottom: 0,
                marginBottom: 0,
              }}>
              {t('forms.shorelineAnalysis.transectsSettings.confirm')}
            </Button>
            <Button
              color="secondary"
              style={{
                padding: 0,
                margin: 0,
              }}
              onClick={() => {
                transectsObjects.forEach((object) => object.setMap(null));
                setTransectsObjects([]);
                setStep('DRAW_BASELINE');
                setIsSettingsTransectOpen(false);
              }}>
              {t('forms.csqueeze.result.cancel')}
            </Button>
          </Card>
        </Box>
      </Grow>
    </div>
  );
};

export default CoastlineAnalysisSteps;
